import type { DayCell as DayCellPayload, HoursParsed } from '~/lib/api/types'
import styles from './day-cell-compact.module.scss'

type Props = {
  cell: DayCellPayload
  date: string
  todayIso: string
}

export function DayCellCompact({ cell, date, todayIso }: Props) {
  switch (cell.kind) {
    case 'working': {
      const hours = formatCompactHours(cell.hours_parsed)
      return (
        <div
          className={styles.cell}
          aria-label={`working ${hours || ''}`.trim()}
          title={cell.plan_excerpt ?? undefined}
        >
          <span className={`${styles.dot} ${styles.dotWorking}`} aria-hidden />
          {hours && <span className={`${styles.hours} mono`}>{hours}</span>}
        </div>
      )
    }
    case 'parse_failed':
      return (
        <div
          className={styles.cell}
          aria-label="parse failed"
          title={cell.hours_raw ?? undefined}
        >
          <span
            className={`${styles.dot} ${styles.dotParseFailed}`}
            aria-hidden
          />
          <span className={styles.hours}>?</span>
        </div>
      )
    case 'ooo':
      // OooBand handles OOO in MonthGrid too; this branch is unused but kept
      // for type exhaustiveness if ever called directly.
      return null
    case 'missing': {
      const cmp = compareIso(date, todayIso)
      if (cmp > 0) return <div className={styles.cellEmpty} aria-hidden />
      return (
        <div
          className={styles.cell}
          aria-label={cmp === 0 ? 'missing today' : 'missing'}
        >
          <span className={`${styles.dot} ${styles.dotMissing}`} aria-hidden />
        </div>
      )
    }
    default:
      return null
  }
}

function formatCompactHours(parsed: HoursParsed): string {
  if (!parsed?.start || !parsed?.end) return ''
  return `${shorth(parsed.start)}–${shorth(parsed.end)}`
}

function shorth(hhmm: string): string {
  const [hStr] = hhmm.split(':')
  const h = Number(hStr)
  if (h === 0) return '12a'
  if (h < 12) return `${h}a`
  if (h === 12) return '12p'
  return `${h - 12}p`
}

function compareIso(a: string, b: string): number {
  if (a < b) return -1
  if (a > b) return 1
  return 0
}
